import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import http from "http";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import { useDispatch, useSelector } from "react-redux";
import { apiUrl } from "config/settings";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomButton from "components/CustomButton";
import useDataManager from "hooks/useDataManager";
import { endpointName } from "./const";
import { METER_TYPE, TIME_FORMAT } from "config/const";
import SelectInput from "components/files/SelectInput";
import TextInput from "components/files/TextInput";
import { createDateWithTime, findKeyByValue, getMaxItemByKey } from "config/functions";
import moment from "moment";
import NullCheck from "components/NullCheck";
import { Divider, Stack, Typography } from "@mui/material";
import { setMeterReader, setQRScanner } from "store/userSettingsSlice";
import CustomIconButton from "components/CustomIconButton";
import { Fingerprint, QrCodeScanner } from "@mui/icons-material";
import CustomDialog from "components/CustomDialog";
import CheckRole from "components/CheckRole";
import { SU_ADMIN } from "config/roles";

const Details = ({ data, setData, list, setList, metersList }) => {
  const { addItem, editItem } = useDataManager();
  const dispatch = useDispatch();
  const { bt } = useBizTypeTranslation();
  const { reset, setValue, handleSubmit, control, watch, setError, formState: { errors } } = useForm();
  const user = useSelector((state) => state.userSettings?.user);
  const [lastRead, setLastRead] = useState(null)
  const [errorDay, setErrorDay] = useState(false)
  const [isBetweenRange, setIsBetweenRange] = useState(true)
  const [meterReaderValue, setMeterReaderValue] = useState(null)
  const [meterData, setMeterData] = useState(null)
  const meter = watch('meter')

  const offPeakValue = watch('offPeakValue')
  const midPeakValue = watch('midPeakValue')
  const onPeakValue = watch('onPeakValue')

  useEffect(() => {
    if (data && data !== "new")
      reset({ ...data, meter: data?.meter?._id });
  }, [data]);

  useEffect(() => {
    if (meter) {
      const listByMeter = list.filter(m => m?.meter?._id === meter)
      const findMeter = metersList?.find(m => m?._id === meter)

      /** Max Meter Read And Is Read Today */
      if (listByMeter.length > 0) {

        const maxMeterRead = getMaxItemByKey(listByMeter, "readValue")
        setLastRead(maxMeterRead?.readValue)
        const lastReadDt = moment(listByMeter[listByMeter?.length - 1]?.created).startOf('day')

        if (data?.id !== "new" || moment(lastReadDt).add(1, 'day').isSameOrBefore(moment().startOf('day')))
          setErrorDay(false)
        else
          setErrorDay(true)
      }
      else {
        setErrorDay(false)
        setLastRead(0)
      }

      /** Check If To Read Is Between Range */
      const startRecommend = findMeter?.startRecommendTimeRead
        ? moment(createDateWithTime(null, findMeter?.startRecommendTimeRead))
        : null

      const endRecommend = findMeter?.endRecommendTimeRead
        ? moment(createDateWithTime(null, findMeter?.endRecommendTimeRead))
        : null

      setIsBetweenRange(startRecommend
        ? moment(moment().format(TIME_FORMAT)).isBetween(startRecommend, endRecommend)
        : true)

      setMeterData(findMeter)
    };
  }, [meter])

  useEffect(() => {
    if (meterData?.isTariff) {
      const sum = parseFloat(offPeakValue || 0) + parseFloat(midPeakValue || 0) + parseFloat(onPeakValue || 0)
      setValue("readValue", sum)
    }
  }, [meterData, offPeakValue, midPeakValue, onPeakValue]);

  const onClose = () => {
    setData(null);
  };

  const onSubmit = (values) => {

    if (data?.id === "new" && lastRead >= values?.readValue) {
      return dispatch(setShowMassage({
        massage: `${bt('NUMBER_MUST_BE_GREATER_THAN')} ${lastRead}`,
        type: "info"
      }));
    }

    dispatch(setShowLoading(true));

    let obj = {
      meter: values?.meter,
      readValue: values?.readValue,
      offPeakValue: values?.offPeakValue,
      midPeakValue: values?.midPeakValue,
      onPeakValue: values?.onPeakValue,
      employee: data?.employee ?? user?.id,
      employeeName: data?.employeeName ?? `${user?.fn} ${user?.ln}`,
      isHappy: true,
    };

    try {
      /** Edit meter Read */
      if (data?.id !== "new") {
        http.patch(`${apiUrl}/${endpointName}/${data?.id}`, obj)
          .then((res) => {
            dispatch(setShowLoading(false));

            if (res?.data?.id === 1) {
              obj._id = data?.id;
              obj.created = data?.created;
              obj.isExceed = res?.data?.data?.isExceed;
              obj.meter = data?.meter;
              const newList = editItem(obj, list);
              setList(newList)
              onClose();
            }

            else {
              dispatch(setShowMassage({ massage: "ERROR", type: "error" }));
            }

          })
      }

      /** Add meter Read */
      else {
        http.post(`${apiUrl}/${endpointName}`, obj).then((res) => {
          dispatch(setShowLoading(false));

          if (res?.data?.id === 1) {
            obj._id = res?.data?.data?.itemId;
            obj.isExceed = res?.data?.data?.isExceed;
            obj.created = moment();
            obj.meter = { ...metersList.find(meter => meter?._id === values?.meter) }
            const newList = addItem(obj, list);
            setList(newList)
            onClose();
          }

          else if (res?.data?.id === -5) {
            dispatch(setShowLoading(false));
            dispatch(setShowMassage({ massage: "PATH_EXISTS", type: "error" }));
            setError("path", {
              type: "path",
              message: bt("PATH_EXISTS"),
            });
          }

          else if (res?.data?.id === -6) {
            dispatch(setShowMassage({ massage: "PATH_EXISTS", type: "error" }));
            setError("userName", {
              type: "userName",
              message: bt("USER_NAME_EXISTS"),
            });
          }

          else {
            dispatch(setShowMassage({ massage: "ERROR", type: "error" }));
          }
        })
      }
    }
    catch (error) {
      dispatch(setShowMassage({ massage: "ERROR", type: "error" }));
      dispatch(setShowLoading(false));
    }

  };

  const openQRScanner = () => {
    dispatch(setQRScanner({
      open: true, callBack: (meterId) => {
        const meter = metersList?.find(m => m?._id === meterId)
        if (meter)
          setValue('meter', meter?._id)
        else
          dispatch(setShowMassage({ massage: "METER_NOT_FOUND", type: "error" }));
      }
    }))
  }

  // const openMeterReader = () => {
  //   dispatch(setMeterReader({
  //     open: true, callBack: (number) => {
  //       setMeterReaderValue(number)
  //     }
  //   }))
  // }

  return (
    <CustomDialog
      open={data ? true : false}
      onClose={onClose}
      title='METER_READ'
      closeBtn="CLOSE"
      actionBtn={{ title: "SAVE", onClick: () => handleSubmit(onSubmit)() }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          height="100%"
          width="100%"
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          paddingBottom={2}
        >
          <Stack spacing={1}>
            <Stack direction='row' alignItems='end' justifyContent='center' spacing={1}>
              <SelectInput
                name="meter"
                addFlex
                title="SELECT_METER"
                control={control}
                required
                disabled={data?.id !== "new"}
                errors={errors}
                defaultValue={data?.meter?._id ?? metersList[0]?.id}
                options={metersList?.map(meter => {
                  return (
                    {
                      id: meter?._id,
                      name: `${bt(findKeyByValue(METER_TYPE, meter?.type))} (${meter?.name})`
                    }
                  )
                })}
              />

              <CustomIconButton
                size={40}
                disabled={data?.id !== "new"}
                icon={<QrCodeScanner />}
                onClick={openQRScanner}
              />
            </Stack>

            <Stack width='100%' spacing={1}>
              <NullCheck valueForCheck={meterData?.isTariff}>
                <TextInput
                  required
                  width='100%'
                  errors={errors}
                  name="offPeakValue"
                  type='number'
                  title="OFF_PEAK_VALUE"
                  control={control}
                />

                <TextInput
                  required
                  width='100%'
                  errors={errors}
                  name="midPeakValue"
                  type='number'
                  title="MID_PEAK_VALUE"
                  control={control}
                />

                <TextInput
                  required
                  width='100%'
                  errors={errors}
                  name="onPeakValue"
                  type='number'
                  title="ON_PEAK_VALUE"
                  control={control}
                />
              </NullCheck>

              <TextInput
                required
                width='100%'
                disabled={meterData?.isTariff}
                errors={errors}
                name="readValue"
                type='number'
                title="READ_VALUE"
                control={control}
              />
            </Stack>

            {/* <CheckRole role={SU_ADMIN}>
              <CustomButton title='METER_PROCESSING' onClick={openMeterReader} />
            </CheckRole> */}

            <NullCheck valueForCheck={lastRead > 0}>
              <Typography variant="body2" textAlign='center' fontWeight='bold'>
                {`${bt("METER_LAST_READ")} - ${lastRead}`}
              </Typography>
            </NullCheck>

            <NullCheck valueForCheck={!isBetweenRange}>
              <Typography
                variant="body2"
                color='warning.main'
                textAlign='center'
                fontWeight='bold'
              >
                {`${bt("IS_NOT_BETWEEN_RANGE")}`}
              </Typography>
            </NullCheck>

            <NullCheck valueForCheck={errorDay}>
              <Typography
                variant="body2"
                color='error.text'
                textAlign='center'
                fontWeight='bold'
              >
                {`${bt("ERROR_DAY")}`}
              </Typography>
            </NullCheck>

            {meterReaderValue
              ? <Typography>
                {`${meterReaderValue?.MeterNumber} - ${meterReaderValue?.readValue}`}
              </Typography>
              : null
            }
          </Stack>
        </Stack>
      </form>
    </CustomDialog>
  );
};
export default Details;
